import React from 'react';
import {
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, CalendarViewDayOutlined, Menu as MenuIcon, Today } from '@mui/icons-material';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { CalendarView, DATE_FORMAT } from './CalendarWrapper';
import DatesCarousel from '../DatesCarousel';
import { useAuth } from '../../session/InternalAuthProvider';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { AccountData, OrderPageData, OrderPageTypes } from '../../api';
import { getOrderPages } from '../../utils/AccountUtils';
import { getInitialCurrentDate } from '../CalendarContext/getInitialCurrentDate';
import TTLLocalStorage, { WEBOOK_SAVED_CURRENT_DATE } from '../../utils/TTLLocalStorage';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { UsersMultiSelect } from '../components/UsersMultiSelect';
import { CompactStats } from './CompactStats';
import { CalendarFilter } from '../CalendarFilter/CalendarFilter';
import { useCalendarFilter } from '../CalendarFilter/CalendarFilterContext';
import { getCalendarLocationIds } from '../CalendarFilter/calendarLocationIds';
import { CapacityStats } from '../utils/capacityCalculationUtils';
import CapacityStatsGrid from '../components/CapacityStatsGrid';
import { AreasToolbarControls } from '../agendaView/AreasToolbarControls';
import { mb } from '../../common/theme/mbTokens';
import { useAreasViewOptional } from '../agendaView/AreasViewContext';
import { PHONE_TOP_COLLAPSE_MS } from '../agendaView/agendaStyles';
import { PreviousDaysOpenButton } from '../agendaView/components/PreviousDaysOpenButton';

type CalendarToolbarProps = {
  previousNextCallbacks: {
    previous: any;
    next: any;
  };
  setNoteDialogOpen: (isNoteDialogOpen: boolean) => void;
  setShiftDialogOpen: (isShiftDialogIsOpen: boolean) => void;
  capacityStats: CapacityStats;
};

export const CalendarToolbar = ({
  previousNextCallbacks,
  setNoteDialogOpen,
  setShiftDialogOpen,
  capacityStats,
}: CalendarToolbarProps) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const { isAtLeastSupervisor, isAdmin } = UseAuthPermissions();
  const allowCalendarCondensedMode = useFeatureFlagEnabled('allow-calendar-condensed-mode');

  const {
    openEditOrderDrawer,
    users,
    selectedUsersIds,
    setSelectedUsersIds,
    openExperienceAvailabilityDrawer,
    setCalendarView,
    setCurrentDate,
    calendarView,
    currentDate,
  } = useCalendar();
  const { isSidebarOpen, setIsSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isOpen: boolean) => void;
  }>();
  const setCurrentDateInternal = (newCurrentDate: string) => {
    setCurrentDate(newCurrentDate);
    TTLLocalStorage.setItem(WEBOOK_SAVED_CURRENT_DATE, newCurrentDate, 86400000 * 7);
    const newUrl = `${window.location.origin}/dashboard/calendar/${newCurrentDate}`;
    window.history.pushState(null, '', newUrl);
  };

  const account = authState.account as unknown as AccountData;
  const { selectedOrderPageIds } = useCalendarFilter();
  // A saved view can outlive the account that could use it, so the toolbar and the content must
  // agree on the same gate.
  const isAreasView = calendarView === CalendarView.AREAS;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  // On a phone the agenda keeps its own sticky search, status chips and strip under this toolbar, so the
  // toolbar is one row (no stats, filter as an icon) and folds away while the list is scrolled.
  const isCompactAgendaToolbar = isAreasView && isSmallScreen;
  const { t } = useTranslation();
  const isPhoneTopCollapsed = useAreasViewOptional()?.isPhoneTopCollapsed ?? false;

  const orderPages = getOrderPages(account);

  const onTodayClicked = () => {
    setCurrentDate(getInitialCurrentDate({ calendarView }));
    const newUrl = `${window.location.origin}/dashboard/calendar/`;
    window.history.pushState(null, '', newUrl);
    TTLLocalStorage.removeItem(WEBOOK_SAVED_CURRENT_DATE);
  };
  // 10 is arbitrary, but shouldn't allow condenced mode for customers with too many order pages.
  const hasLessThen10OrderPageAndTheyAreEvents =
    orderPages.filter((x: OrderPageData) => x.order_page_type === OrderPageTypes.EVENT).length < 10 &&
    orderPages.filter((x: OrderPageData) => x.order_page_type === OrderPageTypes.PRIVATE).length === 0;

  const toolbarContent = (
    <Grid container flexDirection="column">
      {isAdmin && !isAreasView && <CompactStats />}

      <Grid
        // Under the stats row (admins) the gap is 12px; without it (non-admins, or the phone agenda, which has no
        // stats) this row is the first element on the page.
        pt={isCompactAgendaToolbar ? 0 : isAdmin ? 1.5 : mb.layout.pageInset}
        pb={isCompactAgendaToolbar ? 1 : 0}
        container
        alignItems={isCompactAgendaToolbar ? 'center' : 'flex-start'}
        gap={isCompactAgendaToolbar ? 0.5 : 1}
        wrap="nowrap"
        px={mb.layout.pageInset}>
        {isSmallScreen && (
          <Grid>
            <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </IconButton>
          </Grid>
        )}

        {isAreasView && !isSmallScreen ? (
          <Grid item>
            <AreasToolbarControls onGoToToday={onTodayClicked} />
          </Grid>
        ) : calendarView === CalendarView.CONDENSED ? null : isSmallScreen ? null : (
          <Grid item>
            <Button variant="soft" startIcon={<Today />} sx={{ whiteSpace: 'nowrap' }} onClick={onTodayClicked}>
              {t('calendar.today')}
            </Button>
          </Grid>
        )}
        <Grid>
          <FormControl>
            <Select
              style={{
                height: 44,
                width: isCompactAgendaToolbar ? 44 : 'auto',
                minWidth: isCompactAgendaToolbar ? 44 : isSmallScreen ? 90 : 110,
              }}
              className="view-select"
              value={calendarView}
              // Phone agenda: the select shows only an icon (the menu items stay in full).
              {...(isCompactAgendaToolbar && {
                renderValue: () => <CalendarViewDayOutlined sx={{ display: 'block' }} />,
                inputProps: { 'aria-label': t('calendar.view.areas') },
                sx: {
                  '& .MuiSelect-select': { paddingInline: '10px !important' },
                  '& .MuiSelect-icon': { display: 'none' },
                },
              })}
              onChange={(event) => {
                setCalendarView(event.target.value as CalendarView);
                if (event.target.value === 'week') {
                  setCurrentDateInternal(moment(currentDate).startOf('week').format(DATE_FORMAT));
                }
                if (event.target.value === 'month') {
                  setCurrentDateInternal(moment(currentDate).startOf('month').format(DATE_FORMAT));
                }
              }}>
              <MenuItem value={CalendarView.DAY}>{t('calendar.view.day')}</MenuItem>
              <MenuItem value={CalendarView.THREE_DAY}>{t('calendar.view.3day')}</MenuItem>
              <MenuItem value={CalendarView.WEEK}>{t('calendar.view.week')}</MenuItem>
              <MenuItem value={CalendarView.MONTH}>{t('calendar.view.month')}</MenuItem>
              {hasLessThen10OrderPageAndTheyAreEvents || allowCalendarCondensedMode ? (
                <MenuItem value={CalendarView.CONDENSED}>{t('calendar.view.condensed')}</MenuItem>
              ) : null}
              <MenuItem value={CalendarView.AREAS}>{t('calendar.view.areas')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {calendarView === CalendarView.CONDENSED ? null : (
          // Only phones let the date label shrink (with an ellipsis); on wider screens the actions row gives way.
          <Grid item sx={{ minWidth: 0, flexShrink: isSmallScreen ? 1 : 0 }}>
            <DatesCarousel
              currentDate={currentDate}
              setCurrentDate={setCurrentDateInternal}
              calendarView={calendarView}
              previousNextCallbacks={previousNextCallbacks}
              hideArrows={isCompactAgendaToolbar}
              compactFormat={isCompactAgendaToolbar}
            />
          </Grid>
        )}
        {isAreasView && !isCompactAgendaToolbar && (
          <Grid item sx={{ flexShrink: 0 }}>
            <PreviousDaysOpenButton />
          </Grid>
        )}
        {isCompactAgendaToolbar && (
          <>
            <Grid item sx={{ flexShrink: 0 }}>
              <AreasToolbarControls onGoToToday={onTodayClicked} />
            </Grid>
            <Grid item sx={{ flexShrink: 0 }}>
              <CalendarFilter iconOnly />
            </Grid>
          </>
        )}
        {calendarView === CalendarView.CONDENSED || isAreasView ? null : isSmallScreen ? (
          // Sized by its icon and pushed to the end, so the date label keeps the free width on phones.
          <Grid
            container
            justifyContent={'flex-end'}
            gap={0.5}
            alignItems="center"
            sx={{ width: 'auto', flexShrink: 0, marginInlineStart: 'auto' }}>
            <IconButton onClick={onTodayClicked}>
              <Today />
            </IconButton>
          </Grid>
        ) : null}
        {isSmallScreen || calendarView === CalendarView.MONTH || isAreasView ? null : (
          <UsersMultiSelect
            size="small"
            users={users}
            selectedUsersIds={selectedUsersIds}
            setSelectedUsersIds={setSelectedUsersIds}
            minWidth={120}
            visibleLocationIds={getCalendarLocationIds(account, selectedOrderPageIds)}
          />
        )}
        {!isMediumScreen && (
          <Grid container gap={1} alignItems="center" justifyContent={'flex-end'}>
            <Grid item>
              <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                <Button
                  disabled={!isAtLeastSupervisor}
                  variant="outlined"
                  color="inherit"
                  onClick={() => setNoteDialogOpen(true)}>
                  {t('calendar.add_note')}
                </Button>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                <Button
                  disabled={!isAtLeastSupervisor}
                  variant="outlined"
                  color="inherit"
                  onClick={() => setShiftDialogOpen(true)}>
                  {account.shifts_as_employee_unavailability
                    ? t('calendar.add_employee_unavliability')
                    : t('calendar.add_shift')}
                </Button>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                <Button
                  disabled={!isAtLeastSupervisor}
                  variant="outlined"
                  color="inherit"
                  onClick={() => openExperienceAvailabilityDrawer({})}>
                  {t('calendar.add_unavailability')}
                </Button>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                <Button
                  disabled={!isAtLeastSupervisor}
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openEditOrderDrawer({})}>
                  {t('add_order')}
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        )}
      </Grid>

      {!isCompactAgendaToolbar && (
        <Grid
          sx={{
            width: '100%',
            paddingTop: 1.5,
            paddingBottom: 1,
            paddingInline: mb.layout.pageInset,
            gap: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <CalendarFilter />
          {!isSmallScreen && (
            <CapacityStatsGrid capacityStats={capacityStats} calendarView={calendarView} currentDate={currentDate} />
          )}
        </Grid>
      )}
    </Grid>
  );

  return (
    // The phone agenda keeps its page inset above the first visible element on this always-present wrapper, so
    // folding the toolbar does not push the search field against the top edge.
    <Grid sx={isCompactAgendaToolbar ? { paddingTop: mb.layout.pageInset } : undefined}>
      {isCompactAgendaToolbar ? (
        <Collapse in={!isPhoneTopCollapsed} timeout={PHONE_TOP_COLLAPSE_MS}>
          {toolbarContent}
        </Collapse>
      ) : (
        toolbarContent
      )}
    </Grid>
  );
};
